import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TableQuery } from './table-interactions';

interface TableToolbarProps {
  query: TableQuery;
  onChange: (query: TableQuery) => void;
  filter: { key: string; label: string; options: Array<{ value: string; count: number }> } | null;
  shown: number;
  total: number;
  /** Phones show cards, not headers: they sort from a menu. */
  columns: Array<{ key: string; label: string }>;
}

/** Search, one quick filter and the count of a long result table. Nothing is stored. */
export const TableToolbar = ({ query, onChange, filter, shown, total, columns }: TableToolbarProps) => {
  const chip = (active: boolean) => cn(
    'rounded-full border px-2.5 py-0.5 text-xs transition',
    active ? 'border-domino-aubergine bg-domino-aubergine text-white' : 'border-domino-cardBorder bg-domino-white text-domino-ink hover:border-domino-aubergine',
  );
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <label className="relative min-w-0 flex-1 basis-48">
        <span className="sr-only">Rechercher dans le tableau</span>
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-domino-inkSoft" aria-hidden="true" />
        <input type="search" value={query.search} onChange={(event) => onChange({ ...query, search: event.target.value })}
          placeholder="Rechercher" className="w-full rounded-lg border border-domino-cardBorder bg-domino-white py-1.5 pl-8 pr-2 text-sm text-domino-ink placeholder:text-domino-inkSoft focus:border-domino-aubergine focus:outline-none" />
      </label>
      <div className="flex items-center gap-1.5 sm:hidden">
        <label className="flex items-center gap-1.5 text-xs text-domino-inkSoft">
          Trier par
          <select value={query.sort?.key ?? ''} className="rounded-lg border border-domino-cardBorder bg-domino-white px-2 py-1 text-sm text-domino-ink"
            onChange={(event) => onChange({ ...query, sort: event.target.value ? { key: event.target.value, direction: query.sort?.direction ?? 'asc' } : null })}>
            <option value="">Ordre reçu</option>
            {columns.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}
          </select>
        </label>
        {query.sort && (
          <button type="button" className={chip(false)} onClick={() => onChange({ ...query, sort: { ...query.sort!, direction: query.sort!.direction === 'asc' ? 'desc' : 'asc' } })}
            aria-label={query.sort.direction === 'asc' ? 'Ordre croissant, inverser' : 'Ordre décroissant, inverser'}>
            {query.sort.direction === 'asc' ? '↑' : '↓'}
          </button>
        )}
      </div>
      {filter && (
        <div role="group" aria-label={`Filtrer par ${filter.label}`} className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-domino-inkSoft">{filter.label}</span>
          <button type="button" className={chip(!query.filter)} aria-pressed={!query.filter} onClick={() => onChange({ ...query, filter: null })}>Tous</button>
          {filter.options.map((option) => {
            const active = query.filter?.value === option.value;
            return (
              <button key={option.value} type="button" className={chip(active)} aria-pressed={active}
                onClick={() => onChange({ ...query, filter: active ? null : { key: filter.key, value: option.value } })}>
                {option.value} <span className={active ? 'text-white/80' : 'text-domino-inkSoft'}>{option.count}</span>
              </button>
            );
          })}
        </div>
      )}
      {shown !== total && <span className="text-xs text-domino-inkSoft">{shown} sur {total}</span>}
    </div>
  );
};
